/**
 * Acquisition Due Diligence — Data Capture Form.
 *
 * Walks through 7 DD categories, each item gets green/yellow/red + notes.
 * Saves responses via acquisition-assessments edge function.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ACQUISITION_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_DESCRIPTIONS,
  RECOMMENDATION_META,
  getItemsByCategory,
  saveAcquisitionResponse,
  loadAcquisitionResponses,
  calculateCategorySummary,
  calculateOverallRecommendation,
  type AcquisitionCategory,
  type AcquisitionAssessmentItem,
  type AcquisitionAssessmentResponse,
  type AcquisitionCategorySummary,
  type AcquisitionRecommendation,
} from '../../lib/services/acquisitionAssessment'
import { RATING_LABELS, RATING_COLORS, type AssessmentRating } from '../../lib/services/dailyAssessment'

// ── Rating Button ─────────────────────────────────────────────────────────

function RatingButton({
  rating,
  selected,
  onClick,
}: {
  rating: AssessmentRating
  selected: boolean
  onClick: () => void
}) {
  const colors = RATING_COLORS[rating]
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${
        selected
          ? `${colors.bg} ${colors.text} ${colors.border} ring-2 ring-offset-1`
          : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'
      }`}
    >
      {RATING_LABELS[rating]}
    </button>
  )
}

// ── Single Item Row ───────────────────────────────────────────────────────

function ItemRow({
  item,
  response,
  onRate,
  onNote,
  saving,
}: {
  item: AcquisitionAssessmentItem
  response: AcquisitionAssessmentResponse | undefined
  onRate: (rating: AssessmentRating) => void
  onNote: (note: string) => void
  saving: boolean
}) {
  const [localNote, setLocalNote] = useState(response?.note ?? '')
  const [noteTimer, setNoteTimer] = useState<ReturnType<typeof setTimeout> | null>(null)

  // Sync local note when parent response changes — intentional setState in effect
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setLocalNote(response?.note ?? '') }, [response?.note])

  const handleNoteChange = useCallback(
    (value: string) => {
      setLocalNote(value)
      if (noteTimer) clearTimeout(noteTimer)
      const timer = setTimeout(() => onNote(value), 800)
      setNoteTimer(timer)
    },
    [noteTimer, onNote],
  )

  return (
    <div className="v11-dashboard-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900">{item.label}</p>
          <p className="mt-0.5 text-xs text-slate-500">{item.description}</p>
          <p className="mt-1 text-[10px] font-mono text-slate-400">
            {item.standard_ref} | {item.evidence_type.join(', ')}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {([1, 2, 3] as AssessmentRating[]).map((r) => (
            <RatingButton
              key={r}
              rating={r}
              selected={response?.rating === r}
              onClick={() => onRate(r)}
            />
          ))}
        </div>
      </div>
      {response && (
        <div className="mt-2">
          <textarea
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400"
            rows={2}
            placeholder="Notes (optional) — do not include PHI"
            value={localNote}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>
      )}
      {saving && (
        <p className="mt-1 text-[10px] text-slate-400">Saving...</p>
      )}
    </div>
  )
}

// ── Category Section ──────────────────────────────────────────────────────

function CategorySection({
  category,
  items,
  responses,
  summary,
  onRate,
  onNote,
  savingItems,
}: {
  category: AcquisitionCategory
  items: AcquisitionAssessmentItem[]
  responses: Map<string, AcquisitionAssessmentResponse>
  summary: AcquisitionCategorySummary
  onRate: (itemId: string, category: AcquisitionCategory, rating: AssessmentRating) => void
  onNote: (itemId: string, category: AcquisitionCategory, note: string) => void
  savingItems: Set<string>
}) {
  const [expanded, setExpanded] = useState(false)
  const riskColor =
    summary.risk_level === 'high'
      ? 'text-rose-700 bg-rose-50'
      : summary.risk_level === 'moderate'
        ? 'text-amber-700 bg-amber-50'
        : 'text-emerald-700 bg-emerald-50'

  return (
    <section className="v11-section overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <h3 className="text-sm font-bold text-slate-900">{CATEGORY_LABELS[category]}</h3>
          <p className="text-xs text-slate-500">{CATEGORY_DESCRIPTIONS[category]}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-xs text-slate-500">
            {summary.assessed_count}/{summary.item_count}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${riskColor}`}>
            {summary.risk_level}
          </span>
          <span className="text-slate-400">{expanded ? '−' : '+'}</span>
        </div>
      </button>

      {/* Progress bar */}
      <div className="mx-4 mb-3 flex h-1.5 overflow-hidden rounded-full bg-slate-100">
        {summary.green_count > 0 && (
          <div
            className="bg-emerald-500"
            style={{ width: `${(summary.green_count / summary.item_count) * 100}%` }}
          />
        )}
        {summary.yellow_count > 0 && (
          <div
            className="bg-amber-500"
            style={{ width: `${(summary.yellow_count / summary.item_count) * 100}%` }}
          />
        )}
        {summary.red_count > 0 && (
          <div
            className="bg-rose-500"
            style={{ width: `${(summary.red_count / summary.item_count) * 100}%` }}
          />
        )}
      </div>

      {expanded && (
        <div className="space-y-2 px-4 pb-4">
          {items.map((item) => (
            <ItemRow
              key={item.item_id}
              item={item}
              response={responses.get(item.item_id)}
              onRate={(rating) => onRate(item.item_id, category, rating)}
              onNote={(note) => onNote(item.item_id, category, note)}
              saving={savingItems.has(item.item_id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

// ── Main Form ─────────────────────────────────────────────────────────────

export function AcquisitionDDFormPage() {
  const { visitId } = useParams<{ visitId: string }>()
  const [responses, setResponses] = useState<Map<string, AcquisitionAssessmentResponse>>(new Map())
  const [savingItems, setSavingItems] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Load existing responses
  useEffect(() => {
    if (!visitId) return
    setLoading(true)
    loadAcquisitionResponses(visitId)
      .then((data) => {
        const map = new Map<string, AcquisitionAssessmentResponse>()
        for (const r of data) map.set(r.item_id, r)
        setResponses(map)
      })
      .catch((err) => setLoadError((err as Error).message))
      .finally(() => setLoading(false))
  }, [visitId])

  // Save handler
  const handleRate = useCallback(
    async (itemId: string, category: AcquisitionCategory, rating: AssessmentRating) => {
      if (!visitId) return
      const existing = responses.get(itemId)
      const updated: AcquisitionAssessmentResponse = {
        item_id: itemId,
        rating,
        photo_id: existing?.photo_id ?? null,
        document_ids: existing?.document_ids ?? [],
        note: existing?.note ?? '',
        assessed_at: new Date().toISOString(),
      }
      setResponses((prev) => new Map(prev).set(itemId, updated))
      setSavingItems((prev) => new Set(prev).add(itemId))
      try {
        await saveAcquisitionResponse(visitId, { item_id: itemId, category, rating, note: updated.note })
      } catch {
        // Revert on error
        if (existing) {
          setResponses((prev) => new Map(prev).set(itemId, existing))
        } else {
          setResponses((prev) => {
            const next = new Map(prev)
            next.delete(itemId)
            return next
          })
        }
      } finally {
        setSavingItems((prev) => {
          const next = new Set(prev)
          next.delete(itemId)
          return next
        })
      }
    },
    [visitId, responses],
  )

  const handleNote = useCallback(
    async (itemId: string, category: AcquisitionCategory, note: string) => {
      if (!visitId) return
      const existing = responses.get(itemId)
      if (!existing) return
      const updated = { ...existing, note }
      setResponses((prev) => new Map(prev).set(itemId, updated))
      setSavingItems((prev) => new Set(prev).add(itemId))
      try {
        await saveAcquisitionResponse(visitId, {
          item_id: itemId,
          category,
          rating: existing.rating,
          note,
        })
      } finally {
        setSavingItems((prev) => {
          const next = new Set(prev)
          next.delete(itemId)
          return next
        })
      }
    },
    [visitId, responses],
  )

  // Category data
  const categorySections = useMemo(() => {
    const responsesArray = Array.from(responses.values())
    return ACQUISITION_CATEGORIES.map((cat) => ({
      category: cat,
      items: getItemsByCategory(cat),
      summary: calculateCategorySummary(cat, responsesArray),
    }))
  }, [responses])

  const recommendation: AcquisitionRecommendation = useMemo(
    () => calculateOverallRecommendation(categorySections.map((s) => s.summary)),
    [categorySections],
  )
  const recMeta = RECOMMENDATION_META[recommendation]

  const totalAssessed = categorySections.reduce((n, s) => n + s.summary.assessed_count, 0)
  const totalItems = categorySections.reduce((n, s) => n + s.summary.item_count, 0)

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-8 text-center text-sm text-slate-500">
        Loading acquisition assessment...
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Header */}
      <header className="v11-workspace-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-brand text-xl font-bold text-[var(--brand-deep)]">
              Acquisition Due Diligence
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              {totalAssessed}/{totalItems} items assessed
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`rounded-lg border px-3 py-1.5 text-sm font-bold ${recMeta.color}`}>
              {recMeta.label}
            </span>
            {totalAssessed > 0 && (
              <Link
                to={`/assessments/acquisition/${visitId}/report`}
                className="text-xs font-semibold text-[var(--brand-primary)] underline"
              >
                View Report
              </Link>
            )}
            <Link
              to="/assessments/acquisition"
              className="text-xs font-semibold text-[var(--brand-primary)] underline"
            >
              Back
            </Link>
          </div>
        </div>
        {totalAssessed > 0 && (
          <p className="mt-2 text-xs text-slate-500">{recMeta.description}</p>
        )}
      </header>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {loadError}
        </div>
      )}

      {/* Category sections */}
      {categorySections.map(({ category, items, summary }) => (
        <CategorySection
          key={category}
          category={category}
          items={items}
          responses={responses}
          summary={summary}
          onRate={handleRate}
          onNote={handleNote}
          savingItems={savingItems}
        />
      ))}
    </div>
  )
}
